import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { StepperProps } from './stepper.types';
import { SVG } from 'components';
import { Container, StepperButton } from './stepper.styled';
import { Icons } from 'assets';

export const Stepper = observer((inputProps: StepperProps) => {
  const {
    value = 0,
    onChange,
    step = 1,
    min = Number.MIN_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER,
    allowDecimals = false,
    precision = 2,
    disabled,
    style,
    ...rest
  } = inputProps;

  const [currentValue, setCurrentValue] = useState<number>(Number(value) || 0);
  const [inputValue, setInputValue] = useState<string>(String(value || 0));

  useEffect(() => {
    setCurrentValue(Number(value) || 0);
    setInputValue(String(value || 0));
  }, [value]);

  const getClampedValue = (value: number): number =>
    Math.max(min, Math.min(max, value));

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let rawValue = e.target.value;

    if (!allowDecimals && !/^-?\d*$/.test(rawValue)) {
      return;
    }

    rawValue = rawValue.replace(',', '.');

    if (!/^-?\d*\.?\d*$/.test(rawValue)) {
      return;
    }

    if (allowDecimals && rawValue.includes('.')) {
      const [intPart, decPart = '0'] = rawValue.split('.');
      rawValue =
        decPart.length > precision
          ? `${intPart}.${decPart.slice(0, precision)}`
          : rawValue;
    }

    setInputValue(rawValue);

    const numValue = parseFloat(rawValue);

    if (!isNaN(numValue)) {
      const clampedValue = getClampedValue(numValue);
      setCurrentValue(clampedValue);
      onChange?.(clampedValue);
    }
  };

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    let normalizedValue = currentValue;

    if (inputValue === '' || inputValue === '-') {
      normalizedValue = 0;
    } else {
      const numValue = parseFloat(inputValue);
      normalizedValue = isNaN(numValue) ? currentValue : numValue;
    }

    let clampedValue = getClampedValue(normalizedValue);

    if (allowDecimals) {
      const factor = 10 ** precision;
      clampedValue = Math.round(clampedValue * factor) / factor;
    }

    setCurrentValue(clampedValue);
    setInputValue(String(clampedValue));
    onChange?.(clampedValue);
    inputProps.onBlur?.(event);
  };

  const handleStep = (direction: 'up' | 'down') => {
    let newValue = currentValue + (direction === 'up' ? step : -step);
    newValue = getClampedValue(newValue);

    if (allowDecimals) {
      const factor = 10 ** precision;
      newValue = Math.round(newValue * factor) / factor;
    }

    setCurrentValue(newValue);
    setInputValue(String(newValue));
    onChange?.(newValue);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        handleStep('up');

        return;

      case 'ArrowDown':
        e.preventDefault();
        handleStep('down');

        return;

      default:
        return;
    }
  };

  const canDecrement = currentValue > min;
  const canIncrement = currentValue < max;

  return (
    <Container style={style}>
      <StepperButton
        type="button"
        onClick={() => handleStep('down')}
        disabled={!canDecrement || disabled}
        aria-label="Уменьшить"
      >
        <SVG src={Icons.Minus} />
      </StepperButton>

      <input
        {...rest}
        value={inputValue}
        type="text"
        inputMode="decimal"
        step="any"
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        min={min}
        max={max}
      />

      <StepperButton
        type="button"
        onClick={() => handleStep('up')}
        disabled={!canIncrement || disabled}
        aria-label="Увеличить"
      >
        <SVG src={Icons.Plus} />
      </StepperButton>
    </Container>
  );
});
